import React, { useState, useEffect } from "react";
import { FiDownload, FiX, FiCheckCircle, FiExternalLink, FiShare, FiPlusSquare } from "react-icons/fi";
import "./InstallAppModal.css";

export default function InstallAppModal({ isOpen, onClose }) {
  const [prompt, setPrompt] = useState(() => window.deferredPWAInstallPrompt);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const checkInstalled = () => {
      const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true ||
        localStorage.getItem("magizh_pwa_installed") === "true";
      setIsInstalled(standalone);
    };

    checkInstalled();

    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) {
      setIsIOS(true);
    }

    const handlePromptReady = () => {
      setPrompt(window.deferredPWAInstallPrompt);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      localStorage.setItem("magizh_pwa_installed", "true");
    };

    window.addEventListener("pwa-prompt-ready", handlePromptReady);
    window.addEventListener("appinstalled", handleAppInstalled);

    if (window.deferredPWAInstallPrompt) {
      setPrompt(window.deferredPWAInstallPrompt);
    }

    return () => {
      window.removeEventListener("pwa-prompt-ready", handlePromptReady);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [isOpen]);

  const handleInstallClick = async () => {
    if (isInstalled) {
      // Launch or focus app
      window.location.href = "/home";
      onClose();
      return;
    }

    setInstalling(true);

    const activePrompt = prompt || window.deferredPWAInstallPrompt;

    if (activePrompt) {
      try {
        activePrompt.prompt();
        const { outcome } = await activePrompt.userChoice;
        if (outcome === "accepted") {
          setIsInstalled(true);
          localStorage.setItem("magizh_pwa_installed", "true");
          window.deferredPWAInstallPrompt = null;
          setPrompt(null);
        }
      } catch (err) {
        console.warn("PWA install error:", err);
      } finally {
        setInstalling(false);
      }
      return;
    }

    setInstalling(false);
  };

  if (!isOpen) return null;

  return (
    <div className="install-modal-backdrop" onClick={onClose}>
      <div className="install-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="install-modal-close" onClick={onClose} aria-label="Close modal">
          <FiX />
        </button>

        {/* Modal Header Branding */}
        <div className="install-modal-header">
          <div className="install-logo-wrapper">
            <img
              src={`${process.env.PUBLIC_URL || ""}/Assets/logooo.png`}
              alt="Magizh One Logo"
              className="install-app-logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `${process.env.PUBLIC_URL || ""}/app-icon-192.png`;
              }}
            />
          </div>
          <div className="install-header-text">
            <h2>Magizh One App</h2>
            <p>{isInstalled ? "App Installed & Ready" : "One Platform, Many Solutions"}</p>
          </div>
        </div>

        {/* Main Action Area */}
        {isInstalled ? (
          <div className="install-action-box">
            <div className="install-already-badge">
              <FiCheckCircle className="check-icon" />
              <span>Magizh One App Installed</span>
            </div>
            {/* <button
              type="button"
              className="install-primary-btn open-btn"
              onClick={handleInstallClick}
            >
              <FiExternalLink className="btn-download-icon" />
              <span>OPEN APP</span>
            </button> */}
          </div>
        ) : isIOS ? (
          <div className="install-ios-guide">
            <h4>Install on iOS (Safari)</h4>
            <div className="ios-guide-step">
              <span className="step-badge">1</span>
              <span>Tap the <strong>Share</strong> button <FiShare className="inline-icon" /> at the bottom.</span>
            </div>
            <div className="ios-guide-step">
              <span className="step-badge">2</span>
              <span>Scroll & tap <strong>Add to Home Screen</strong> <FiPlusSquare className="inline-icon" />.</span>
            </div>
            <div className="ios-guide-step">
              <span className="step-badge">3</span>
              <span>Tap <strong>Add</strong> top-right to finish!</span>
            </div>
          </div>
        ) : (
          <div className="install-action-box">
            <button
              type="button"
              className="install-primary-btn"
              onClick={handleInstallClick}
              disabled={installing}
            >
              <FiDownload className="btn-download-icon" />
              <span>{installing ? "Installing..." : "INSTALL NOW"}</span>
            </button>
          </div>
        )}

        <div className="install-modal-footer">
          <span>Official Magizh One Enterprise Web Application</span>
        </div>
      </div>
    </div>
  );
}
